import { Link } from 'react-router-dom';

/** 对照设计稿：3+2 布局（首行三张，次行价格与权益） */
const CARDS = [
  {
    to: '/leads',
    icon: 'ri-radar-line',
    title: '潜在线索池',
    desc: '按地区、行业、事件类型筛选公开信息形成的候选服务机会，未经验证，先看线索再动手。',
    action: '浏览线索',
    tone: 'primary',
    bg: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20radar%20rings%20and%20network%20node%20lines%20representing%20opportunity%20discovery%2C%20soft%20sage%20green%20and%20warm%20cream%20tones%2C%20clean%20light%20background%2C%20elegant%20editorial%20flat%20illustration%2C%20harmonious%20gentle%20gradient%2C%20no%20text&width=800&height=600&seq=ti-feat-leads-2026&orientation=landscape',
  },
  {
    to: '/coming-soon/thinktank',
    icon: 'ri-book-2-line',
    title: '行业智库',
    desc: '政策解读、行业观察与风险提示，帮助判断线索背后的业务逻辑与核验方向。',
    action: '打开知识库',
    tone: 'secondary',
    bg: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20stacked%20books%20and%20knowledge%20line%20shapes%20with%20a%20small%20document%20icon%2C%20soft%20sage%20green%20and%20warm%20cream%20tones%2C%20clean%20light%20background%2C%20elegant%20editorial%20flat%20illustration%2C%20harmonious%20gentle%20gradient%2C%20no%20text&width=800&height=600&seq=ti-feat-insights-2026&orientation=landscape',
  },
  {
    to: '/coming-soon/bp',
    icon: 'ri-flow-chart',
    title: '税务合规BP工作台',
    desc: '面向单事项的专业工作台：事实确认到方案对比、测算、复核与交付，可独立购买使用。',
    action: '进入工作台',
    tone: 'primary',
    bg: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20workflow%20blueprint%20with%20connected%20process%20step%20blocks%20and%20nodes%2C%20soft%20sage%20green%20and%20warm%20cream%20tones%2C%20clean%20light%20background%2C%20elegant%20editorial%20flat%20illustration%2C%20harmonious%20gentle%20gradient%2C%20no%20text&width=800&height=600&seq=ti-feat-bp-2026&orientation=landscape',
  },
  {
    to: '/coming-soon/boss',
    icon: 'ri-user-star-line',
    title: 'BOSS经营智能体',
    desc: '面向企业老板的经营问题与决策界面：指标、异常、情景模拟与决策建议，不做财务后台。',
    action: '进入智能体',
    tone: 'accent',
    bg: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20rising%20business%20growth%20charts%20and%20decision%20dashboard%20shapes%20with%20gentle%20trend%20lines%2C%20deep%20ink%20green%20and%20soft%20cream%20tones%2C%20clean%20light%20background%2C%20elegant%20editorial%20flat%20illustration%2C%20harmonious%20gentle%20gradient%2C%20no%20text&width=800&height=600&seq=ti-feat-boss-2026&orientation=landscape',
  },
  {
    to: '/coming-soon/orders',
    icon: 'ri-vip-diamond-line',
    title: '价格与权益',
    desc: '基础订阅、深度分析增值服务，以及税务BP工作台与 BOSS 经营智能体的独立权益说明。',
    action: '查看价格',
    tone: 'secondary',
    bg: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20pricing%20cards%20and%20diamond%20shapes%20with%20gentle%20layers%2C%20soft%20sage%20green%20and%20warm%20cream%20tones%2C%20clean%20light%20background%2C%20elegant%20editorial%20flat%20illustration%2C%20harmonious%20gentle%20gradient%2C%20no%20text&width=800&height=600&seq=ti-feat-pricing-2026&orientation=landscape',
  },
];

const toneMap: Record<
  string,
  { bubble: string; overlay: string; border: string; action: string; ring: string }
> = {
  primary: {
    bubble: 'bg-primary-600 text-background-50',
    overlay: 'from-primary-500/35 via-background-50/60 to-background-50/25',
    border: 'border-primary-200 hover:border-primary-500',
    action: 'text-primary-700',
    ring: 'group-hover:ring-primary-400/40',
  },
  accent: {
    bubble: 'bg-accent-500 text-foreground-950',
    overlay: 'from-accent-500/40 via-background-50/58 to-background-50/22',
    border: 'border-accent-200 hover:border-accent-400',
    action: 'text-accent-700',
    ring: 'group-hover:ring-accent-400/40',
  },
  secondary: {
    bubble: 'bg-secondary-600 text-background-50',
    overlay: 'from-secondary-500/35 via-background-50/60 to-background-50/25',
    border: 'border-secondary-200 hover:border-secondary-500',
    action: 'text-secondary-700',
    ring: 'group-hover:ring-secondary-400/40',
  },
};

const CARD_CLASS =
  'group relative flex cursor-pointer flex-col items-start overflow-hidden rounded-lg border bg-background-50 p-6 ring-1 ring-inset ring-transparent transition-all duration-300 hover:-translate-y-1';

export default function FeatureGrid() {
  return (
    <section className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8 md:py-20">
      <div className="mb-10 max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-primary-700">核心能力</span>
        <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
          从一个想法到一条交付路径
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          不同模块互相衔接，也可以独立使用。先发现线索，再验证机会，然后进入专业交付或经营决策。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((card) => {
          const tone = toneMap[card.tone];
          return (
            <Link
              key={card.title}
              to={card.to}
              className={`${CARD_CLASS} ${tone.border} ${tone.ring}`}
            >
              <img
                src={card.bg}
                alt=""
                aria-hidden
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-80 transition-opacity duration-300 group-hover:opacity-100"
              />
              <span
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${tone.overlay}`}
              />
              <span
                className={`relative mb-5 flex h-12 w-12 items-center justify-center rounded-lg ${tone.bubble}`}
              >
                <i className={`${card.icon} text-2xl`} />
              </span>
              <h3 className="relative font-heading text-lg font-semibold text-foreground-900">
                {card.title}
              </h3>
              <p className="relative mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                {card.desc}
              </p>
              <span
                className={`relative mt-5 inline-flex items-center gap-1 text-sm font-medium ${tone.action} transition-transform duration-300 group-hover:translate-x-0.5`}
              >
                {card.action}
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
